import { createBrowserRouter, createHashRouter, Navigate, type RouteObject } from 'react-router';
import { apiMode } from '@/api/mode';
import { ALL_MODULES } from '@/app/registry';
import { NoAccess } from '@/components/QueryBoundary';
import { lazyComponent } from '@/platform/lazyComponent';
import { ModuleGate } from '@/platform/moduleContext';
import { Require } from '@/platform/Require';
import { BootstrapGate } from '@/platform/session';

const ForgotPasswordPage = lazyComponent(() => import('@modules/base/desktop/login/ForgotPasswordPage').then((module) => module.ForgotPasswordPage));
const LoginPage = lazyComponent(() => import('@modules/base/desktop/login/LoginPage').then((module) => module.LoginPage));
const RedeemCodePage = lazyComponent(() => import('@modules/base/desktop/login/RedeemCodePage').then((module) => module.RedeemCodePage));
const SettingsLayout = lazyComponent(() => import('@modules/base/desktop/settings/SettingsLayout').then((module) => module.SettingsLayout));
const SetupPage = lazyComponent(() => import('@modules/base/desktop/setup/SetupPage').then((module) => module.SetupPage));
const BodyMapDemo = import.meta.env.VITE_API_MODE === 'mock' ? lazyComponent(() => import('@modules/physio/desktop/body-map/BodyMapDemo').then((module) => module.BodyMapDemo)) : null;
const AppShell = lazyComponent(() => import('@/shell/AppShell').then((module) => module.AppShell));
const Home = lazyComponent(() => import('./Home').then((module) => module.Home));

/**
 * The router is assembled from the modules, the way `api/urls.py` assembles the backend: it holds no page of its own. Each module's
 * pages, reports and settings pages sit behind a gate that answers "not found" while the module is not installed for the organization.
 */

/** Adds the pages other modules mount under a route of this one (matched by the route's `id`). */
function mountExtensions(routes: RouteObject[]): RouteObject[] {
  return routes.map((route) => {
    let children = route.children ? mountExtensions(route.children) : undefined;
    if (route.id) {
      const mounted = ALL_MODULES.flatMap((m) => {
        const extra = m.routeExtensions?.[route.id!];
        return extra ? [{ element: <ModuleGate id={m.id} />, children: extra } satisfies RouteObject] : [];
      });
      if (mounted.length) children = [...(children ?? []), ...mounted];
    }
    return children ? ({ ...route, children } as RouteObject) : route;
  });
}

const modulePages: RouteObject[] = ALL_MODULES.map((m) => ({
  element: <ModuleGate id={m.id} />,
  children: mountExtensions([
    ...(m.routes ?? []),
    ...(m.reports ?? []).map((r): RouteObject => ({ path: `reports/${r.slug}`, element: <Require permission={r.permission}><r.component /></Require> })),
  ]),
}));

const settingsPages: RouteObject[] = ALL_MODULES.flatMap((m) =>
  (m.settingsSections ?? []).filter((s) => apiMode === 'mock' || s.live).map(
    (s): RouteObject => ({
      path: s.path,
      element: (
        <ModuleGate id={m.id}>
          <Require permission={s.permission}>
            <s.component />
          </Require>
        </ModuleGate>
      ),
    }),
  ),
);

const routes: RouteObject[] = [
  // A developer preview; never part of the real service's build.
  ...(BodyMapDemo ? [{ path: 'dev/body-map', element: <BodyMapDemo /> }] : []),
  { path: 'setup', element: <SetupPage /> },
  // The pages a person sees before they are signed in. They wear the organization's look from a public answer, so none needs a session.
  { path: 'login', element: <LoginPage /> },
  { path: 'forgot-password', element: <ForgotPasswordPage /> },
  { path: 'reset-password', element: <RedeemCodePage purpose="reset" /> },
  { path: 'accept-invitation', element: <RedeemCodePage purpose="invitation" /> },
  {
    element: (
      <BootstrapGate>
        <AppShell />
      </BootstrapGate>
    ),
    children: [
      { index: true, element: <Home /> },
      ...modulePages,
      {
        path: 'settings',
        element: <Require permission={['settings.manage', 'backup.manage']}><SettingsLayout /></Require>,
        children: [{ index: true, element: <Navigate to="organization" replace /> }, ...settingsPages],
      },
      { path: '*', element: <NoAccess title="Page not found" description="The page you are looking for does not exist." /> },
    ],
  },
];

// Tauri serves the app from a custom protocol; hash routing avoids any SPA-fallback dependency there.
export const router = import.meta.env.VITE_ROUTER === 'hash' ? createHashRouter(routes) : createBrowserRouter(routes);
